import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Link as LinkDoRouter,
  Navigate as NavigateDoRouter,
  matchPath,
  useLocation
} from 'react-router-dom';
import { HREFLANG, PAGINAS, comPrefixo, lerPrefixo } from '../config/seo';
import { traduzirCaminho } from '../config/posts';
import en from './en';
import pt from './pt';
import es from './es';

/**
 * Tradução da interface.
 *
 * ## Por que não `react-i18next`
 *
 * Ele é o padrão, e resolve dois problemas de verdade: interpolação e regras de
 * plural. Só que as regras de plural de inglês, português e espanhol são a
 * mesma — uma forma para 1, outra para o resto —, e a interpolação aqui é
 * substituir `{nome}` numa string. Trazer três pacotes para isso seria pagar
 * por generalidade que estes três idiomas não usam.
 *
 * O ponto em que essa conta vira: um idioma com mais de duas categorias de
 * plural — russo, polonês, árabe. Aí a tabela abaixo passa a mentir, e a troca
 * pelo i18next deixa de ser preferência.
 *
 * ## Como usar
 *
 *     const { t, idioma, trocarIdioma } = useIdioma();
 *     t('app.projetos')                    // "Projects"
 *     t('projetos.total', { n: 3 })        // "3 projects"
 *     t('projetos.total', { n: 1 })        // "1 project"
 *
 * ## Chave ausente
 *
 * Devolve a própria chave, visível na tela. É deliberado: um texto faltando
 * precisa aparecer como `projetos.titulo` e não como espaço em branco, senão
 * a falha se esconde justamente onde ninguém procura — numa tela em espanhol
 * que quem desenvolve não abre.
 */

const DICIONARIOS = { en, pt, es };

export const IDIOMAS = [
  { codigo: 'en', nome: 'English' },
  { codigo: 'pt', nome: 'Português' },
  { codigo: 'es', nome: 'Español' }
];

const PADRAO = 'en';
const GUARDADO = 'silentpuma:idioma';

/* As páginas que existem em três URLs (`/plans`, `/pt-br/plans`, `/es/plans`):
   as indexáveis e os posts — as mesmas que o pré-render grava. */
const PUBLICAS = [...PAGINAS.filter((p) => p.indexar).map((p) => p.caminho), '/blog/:slug'];
const ehPublica = (caminho) => PUBLICAS.some((padrao) => matchPath(padrao, caminho));

/**
 * O idioma que a URL impõe: o do prefixo, ou inglês numa pública sem prefixo.
 * `null` fora das públicas — lá manda a conta, ou a última tela vista.
 */
export const idiomaDaRota = (pathname) => {
  const { idioma, caminho } = lerPrefixo(pathname);
  return idioma || (ehPublica(caminho) ? PADRAO : null);
};

/**
 * O destino no idioma pedido. Só as públicas têm versão por idioma; área
 * logada e fluxos de conta passam como vieram.
 */
export const caminhoNo = (idioma, destino) => {
  const corte = destino.search(/[?#]/);
  const caminho = corte < 0 ? destino : destino.slice(0, corte);
  return ehPublica(caminho)
    ? comPrefixo(idioma, traduzirCaminho(caminho, idioma)) + destino.slice(caminho.length)
    : destino;
};

/**
 * Idioma das telas que nem a URL nem a conta decidem: esqueci a senha, nova
 * senha pelo link do e-mail, a volta do Stripe, o 404 sem sessão.
 *
 * A ordem é escolha guardada, depois navegador. A guardada é o idioma da
 * última tela vista — quem clica em "Esqueceu a senha?" em /pt-br/login chega
 * em português —, e é ela também que evita o piscar na área logada: sem o
 * valor guardado, a tela nasceria no idioma do navegador e trocaria quando
 * `/me` respondesse.
 */
const idiomaDeEntrada = () => {
  /* No pré-render (scripts/preRenderizar.js) não há navegador: a página sai em
     inglês, que é o que o Googlebot pediria. */
  if (typeof window === 'undefined') return PADRAO;
  try {
    const guardado = window.localStorage.getItem(GUARDADO);
    if (DICIONARIOS[guardado]) return guardado;
  } catch {
    /* localStorage bloqueado (navegação privada, cookies de terceiros): não é
       motivo para a aplicação não subir. */
  }
  const doNavegador = (window.navigator?.languages || [window.navigator?.language || ''])
    .map((tag) => String(tag).slice(0, 2).toLowerCase())
    .find((codigo) => DICIONARIOS[codigo]);
  return doNavegador || PADRAO;
};

const guardar = (idioma) => {
  try {
    window.localStorage.setItem(GUARDADO, idioma);
  } catch {
    /* idem: perder a memória da escolha é degradação aceitável. */
  }
};

/** Lê a chave `a.b.c` no objeto aninhado. */
const buscar = (dicionario, chave) =>
  chave.split('.').reduce((no, parte) => (no == null ? undefined : no[parte]), dicionario);

/**
 * Escolhe a forma singular ou plural.
 *
 * Uma entrada plural é `{ um, outros }`. Vale para os três idiomas: nenhum
 * deles tem categoria além dessas duas.
 *
 * `quantidade` existe para quando `n` já vem formatado — `numero(1284)` é a
 * string "1.284", e `Number("1.284")` não é 1284. Nesses casos o chamador passa
 * o contador cru em `quantidade` e o texto formatado em `n`.
 */
const escolherForma = (valor, vars) => {
  if (typeof valor === 'string') return valor;
  if (valor && typeof valor === 'object' && 'um' in valor) {
    const quantidade = vars?.quantidade ?? vars?.n;
    return Number(quantidade) === 1 ? valor.um : valor.outros;
  }
  return undefined;
};

const interpolar = (texto, vars) =>
  vars
    ? texto.replace(/\{(\w+)\}/g, (_, k) => (vars[k] == null ? `{${k}}` : String(vars[k])))
    : texto;

/**
 * Traduz.
 *
 * Cai no inglês antes de cair na chave: uma tradução que ainda não existe em
 * espanhol deve mostrar o texto em inglês, que é legível, e não o identificador
 * interno. A chave crua só aparece quando nem o inglês tem a entrada — aí é
 * defeito de verdade, e precisa ser visível.
 */
export const traduzir = (idioma, chave, vars) => {
  const noIdioma = escolherForma(buscar(DICIONARIOS[idioma] || {}, chave), vars);
  const emIngles = escolherForma(buscar(DICIONARIOS[PADRAO], chave), vars);
  const texto = noIdioma ?? emIngles;
  return texto == null ? chave : interpolar(texto, vars);
};

const Contexto = createContext(null);

/**
 * Fica dentro do Router: nas páginas públicas o idioma vem da URL, e a conta
 * não alcança — nem o seletor do site alcança a área logada, que segue
 * `user.language`. Sem nenhum dos dois, vale a última tela vista.
 */
export const ProvedorDeIdioma = ({ idiomaDaConta, children }) => {
  const alvo =
    idiomaDaRota(useLocation().pathname) || (DICIONARIOS[idiomaDaConta] ? idiomaDaConta : null);
  const [idioma, setIdioma] = useState(() => alvo || idiomaDeEntrada());

  /* Segue o alvo durante a renderização, não num efeito: com efeito, sair de
     /pt-br para a área de uma conta em espanhol pintaria um quadro em
     português. E só quando o alvo muda, para o seletor do perfil poder trocar
     antes de o servidor confirmar. */
  const [alvoAnterior, setAlvoAnterior] = useState(alvo);
  if (alvo !== alvoAnterior) {
    setAlvoAnterior(alvo);
    if (alvo) setIdioma(alvo);
  }

  /* Guarda o idioma efetivo: é o que `apiFetch` manda no `Accept-Language`, e
     o que as telas sem URL nem conta herdam. */
  useEffect(() => {
    guardar(idioma);
    document.documentElement.lang = HREFLANG[idioma];
  }, [idioma]);

  const trocarIdioma = useCallback((novo) => {
    if (!DICIONARIOS[novo]) return;
    /* Grava antes de agendar o estado, e não só no efeito acima.
       `apiFetch` lê esta chave para montar o `Accept-Language`, e quem troca o
       idioma normalmente dispara uma chamada na linha seguinte — o efeito só
       roda depois do commit, então a requisição que salva a escolha sairia
       pedindo resposta no idioma que a pessoa acabou de abandonar. */
    guardar(novo);
    setIdioma(novo);
  }, []);

  const valor = useMemo(
    () => ({ idioma, trocarIdioma, t: (chave, vars) => traduzir(idioma, chave, vars) }),
    [idioma, trocarIdioma]
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
};

/**
 * Fora do provedor devolve tradução em inglês em vez de quebrar.
 *
 * As telas públicas não têm sessão, e nada impede um componente de ser usado
 * nos dois lados — o Header e o Footer são. Um erro de contexto ali derrubaria
 * a home inteira por causa de um texto.
 */
export const useIdioma = () => {
  const ctx = useContext(Contexto);
  return (
    ctx || {
      idioma: PADRAO,
      trocarIdioma: () => {},
      t: (chave, vars) => traduzir(PADRAO, chave, vars)
    }
  );
};

/**
 * O `Link` do router, com o destino no idioma da tela: `/signup` numa página
 * em português leva a `/pt-br/signup`, e na área logada à versão no idioma da
 * conta. `idioma` pede outro — é o que o seletor usa.
 */
export const Link = ({ to, idioma: pedido, ...props }) => {
  const { idioma } = useIdioma();
  return <LinkDoRouter to={typeof to === 'string' ? caminhoNo(pedido || idioma, to) : to} {...props} />;
};

/** Idem para o `Navigate`. */
export const Navigate = ({ to, ...props }) => {
  const { idioma } = useIdioma();
  return <NavigateDoRouter to={caminhoNo(idioma, to)} {...props} />;
};
